
import { useState } from 'react';
import { Assessment } from '@/types/assessment';
import { createResponse } from '@/services/responseService';
import { calculateScoresByCategory } from './responseUtils';
import { calculateMaturityScores } from './utils/scoreCalculations';
import { processDetailedAnswers } from './utils/answerProcessing';

/**
 * Hook for handling assessment submission
 */
export function useAssessmentSubmission(
  assessment: Assessment,
  categories: any[],
  responses: Record<string, any>
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  /**
   * Process and submit the assessment responses to the API
   */
  const submitAssessment = async (personalInfo: {
    fullName: string;
    email: string;
    company: string;
    designation: string;
    consentGiven: boolean;
  }): Promise<boolean> => {
    try {
      setIsSubmitting(true);
      
      // Calculate scores by category - use the passed responses directly to avoid side effects
      const scoresByCategory = calculateScoresByCategory(
        responses,
        categories,
        assessment.assessmentType || 'statusObjective'
      );
      
      // Calculate maturity scores using the updated function that accepts responses
      const maturityScores = calculateMaturityScores(
        responses,
        assessment.categories.flatMap(cat => cat.questions),
        categories
      );
      
      console.log('[useAssessmentSubmission] Calculated maturity scores:', maturityScores);
      
      // Process detailed answer information - this contains all necessary response data
      const answerDetails = processDetailedAnswers(
        responses,
        assessment.categories
      );
      
      // Format personal info for API
      const formattedPersonalInfo = {
        name: personalInfo.fullName,
        fullName: personalInfo.fullName,
        email: personalInfo.email,
        company: personalInfo.company,
        designation: personalInfo.designation,
        consentGiven: personalInfo.consentGiven
      };

      // Prepare full response data - remove response_data field which doesn't exist in schema
      const responseData = {
        assessment_id: assessment.id,
        assessment_version: assessment.version,
        personal_info: formattedPersonalInfo,
        scores_by_category: scoresByCategory,
        maturity_scores: maturityScores,
        answer_details: answerDetails
      };
      
      // Log the data being sent (without infinite loop-causing side effects)
      console.log('[useAssessmentSubmission] Submitting assessment:', JSON.parse(JSON.stringify(responseData)));

      // Submit to the API
      await createResponse(responseData);
      
      return true;
    } catch (error) {
      console.error('Error submitting assessment:', error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitAssessment,
    isSubmitting
  };
}
